/**
 * Cronograma de Contas a Receber — barras dos 4 buckets por dias até a
 * liquidação (d0_30 / d31_60 / d61_90 / d90_mais). Cor de ENTRADA (turquesa
 * = indra-positivo); a semântica também vem do rótulo/ícone (acessibilidade,
 * não só cor). Realça o próximo 6DU (calendário CCEE) numa faixa acima.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CalendarClock, TrendingUp } from "lucide-react";
import type { CronogramaReceber } from "../../api/types";
import { fmtBRLcompact } from "../../utils/formatters";
import { formatarDataCurtaBr } from "../../utils/dateBr";

const COR_ENTRADA = "#3DDC97"; // indra-positivo (entrada)
const COR_PROXIMO = "#7dd3fc"; // sky-300 — realce do bucket que contém o próximo 6DU

const BUCKETS: Array<{ key: keyof CronogramaReceber; label: string }> = [
  { key: "d0_30", label: "0–30 dias" },
  { key: "d31_60", label: "31–60 dias" },
  { key: "d61_90", label: "61–90 dias" },
  { key: "d90_mais", label: "> 90 dias" },
];

interface Row {
  label: string;
  valor: number;
  destaque: boolean; // bucket que contém o próximo 6DU
}

function parseIso(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

/** Em qual bucket cai o próximo 6DU (dias entre as-of e a data do 6DU). */
function bucketDoProximo6du(asof: string, prox: string | null): string | null {
  if (!prox) return null;
  const dias = Math.round(
    (parseIso(prox).getTime() - parseIso(asof).getTime()) / 86_400_000,
  );
  if (dias < 0) return null;
  if (dias <= 30) return "0–30 dias";
  if (dias <= 60) return "31–60 dias";
  if (dias <= 90) return "61–90 dias";
  return "> 90 dias";
}

interface TooltipEntry {
  payload: Row;
}

function ChartTooltip({ active, payload }: { active?: boolean; payload?: TooltipEntry[] }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload;
  return (
    <div
      className="bg-indra-azul-deep border border-white/10 rounded-md p-2.5 text-xs text-white/90"
      data-testid="cronograma-tooltip"
    >
      <div className="font-semibold text-white mb-0.5">{p.label}</div>
      <div className="font-mono text-indra-positivo">{fmtBRLcompact(p.valor)}</div>
      {p.destaque && <div className="text-[10px] text-sky-300 mt-0.5">contém o próximo 6DU</div>}
    </div>
  );
}

interface Props {
  cronograma: CronogramaReceber;
  asof: string;
}

export function ContasReceberCronograma({ cronograma, asof }: Props) {
  const destaqueLabel = bucketDoProximo6du(asof, cronograma.proximo_6du_data);
  const rows: Row[] = BUCKETS.map((b) => ({
    label: b.label,
    valor: Number(cronograma[b.key] ?? 0),
    destaque: b.label === destaqueLabel,
  }));

  return (
    <div
      className="rounded-xl border border-white/10 bg-indra-azul-surface p-4"
      data-testid="contas-receber-cronograma"
    >
      <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-bold text-white flex items-center gap-1.5">
            <TrendingUp size={15} className="text-indra-positivo" />
            Cronograma de recebimento
          </h2>
          <p className="text-[11px] text-white/50 mt-0.5">Por dias até a liquidação (a partir de hoje)</p>
        </div>
        {cronograma.proximo_6du_data && (
          <div
            className="inline-flex items-center gap-1.5 rounded-lg border border-sky-400/30 bg-sky-400/10 px-3 py-1.5 text-xs"
            data-testid="cronograma-proximo-6du"
          >
            <CalendarClock size={13} className="text-sky-300" />
            <span className="text-white/70">Próximo 6DU</span>
            <span className="text-sky-300 font-semibold">
              {formatarDataCurtaBr(parseIso(cronograma.proximo_6du_data))}
            </span>
            <span className="text-white/40">·</span>
            <span className="text-sky-300 font-semibold">{fmtBRLcompact(cronograma.proximo_6du_valor)}</span>
          </div>
        )}
      </div>

      <div className="h-[240px]" data-testid="cronograma-canvas">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 18, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              stroke="rgba(255,255,255,0.5)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
            />
            <YAxis
              stroke="rgba(255,255,255,0.5)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              tickFormatter={(v: number) => fmtBRLcompact(v)}
              width={72}
            />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Bar dataKey="valor" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {rows.map((r) => (
                <Cell key={r.label} fill={r.destaque ? COR_PROXIMO : COR_ENTRADA} />
              ))}
              <LabelList
                dataKey="valor"
                position="top"
                formatter={(v: number) => fmtBRLcompact(v)}
                fill="rgba(255,255,255,0.7)"
                fontSize={10}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
